import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Copy, KeyRound, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useActivateLicense, useBuyLicense, useLicense } from "@/hooks/use-license";
import { forgetLicense, LICENSE_PRICE, maskKey } from "@/lib/license";

const schema = z.object({ key: z.string().trim().min(1, "Paste your license key").max(200) });

/** Why a key is needed per browser, said once wherever the license is bought or shown. */
export const NO_ACCOUNT_NOTE =
  "There are no accounts: your key unlocks this browser. It's also in your receipt email, so you can unlock other browsers and devices, or this one again after clearing its data.";

function ActivateForm() {
  const activate = useActivateLicense();
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { key: "" },
  });
  const onSubmit = form.handleSubmit(async ({ key }) => {
    try {
      await activate.mutateAsync(key);
    } catch (error) {
      form.setError("key", {
        message: error instanceof Error ? error.message : "Could not check the key",
      });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-2" data-testid="license-form">
        <FormField
          control={form.control}
          name="key"
          render={({ field }) => (
            <FormItem>
              <FormLabel>I already have a key</FormLabel>
              <div className="flex gap-2">
                <FormControl>
                  <Input placeholder="Paste your license key" autoComplete="off" {...field} />
                </FormControl>
                <Button type="submit" variant="outline" disabled={activate.isPending}>
                  <KeyRound />
                  {activate.isPending ? "Checking..." : "Activate"}
                </Button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}

function ActiveLicense({ licenseKey }: { licenseKey: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(licenseKey);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-3" data-testid="license-active">
      <p className="flex items-center gap-2 text-sm font-medium">
        <ShieldCheck className="size-4 text-success" />
        License active: no ads in this browser. Thank you!
      </p>
      <div className="flex items-center gap-2 rounded-md bg-secondary/50 px-3 py-2 font-mono text-sm">
        <span className="flex-1 truncate">{maskKey(licenseKey)}</span>
        <Button variant="ghost" size="sm" onClick={() => void copy()}>
          {copied ? <Check className="text-success" /> : <Copy />}
          {copied ? "Copied" : "Copy key"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{NO_ACCOUNT_NOTE}</p>
      <Button
        variant="link"
        size="sm"
        className="h-auto px-0 text-muted-foreground"
        onClick={forgetLicense}
      >
        Remove the key from this browser
      </Button>
    </div>
  );
}

/** "Remove ads": buy the lifetime license or enter a key; shows the key once active. */
export function LicensePanel() {
  const license = useLicense();
  const buy = useBuyLicense();
  if (license) return <ActiveLicense licenseKey={license.key} />;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm">
          Quick Planning Poker is free and paid for by a few ads. A lifetime license removes them
          all: <span className="font-semibold">{LICENSE_PRICE}, once</span>, no subscription.
        </p>
        <p className="text-xs text-muted-foreground">{NO_ACCOUNT_NOTE}</p>
      </div>
      <Button className="w-full" onClick={() => buy.mutate()} disabled={buy.isPending}>
        {buy.isPending ? "Opening checkout..." : `Buy lifetime license, ${LICENSE_PRICE}`}
      </Button>
      <ActivateForm />
    </div>
  );
}
